(ns frontend.ux-overhaul.listing.preview.model
  "The pure model of the record preview of a listing: the card config of the preview and the
   request that loads one record. The preview shows the details of one record, for example a
   deal, in a panel at the right of the listing. It shows the title of the record and every
   field of the entity type that the user can read and that has a value. The fields are shown
   as the cards of the Kanban show them, so the values have the same text as in the grid
   and on the cards."
  (:require
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entities.pipeline :as pipeline]
   [ui.view.list-opensearch.event :as list-event]))

(def excluded-data-types
  "The data types that the preview does not show as a row. Their values are not a short text:
   rich text is HTML, a file is a list of attachments, and the financial data types and the
   EDN text are structures."
  #{:data.type/text-richtext
    :data.type/file
    :data.type/financials
    :data.type/tesi-financials
    :data.type/tesi-financials-2
    :data.type/text-edn})

(defn normalize-field-path
  "A field path as a vector, or nil when the value is not a field path."
  [field-path]
  (cond
    (vector? field-path) field-path
    (keyword? field-path) [field-path]
    (sequential? field-path) (vec field-path)
    :else nil))

(defn- root-field-path?
  [field-path]
  (= 1 (count field-path)))

(defn previewable-field-path?
  "True when the preview shows the field as a row. A field that comes through a reference has no
   row, because its label is the label of its last attribute and does not tell the reference. A
   field of a data type in `excluded-data-types` has no row. `field-schema-fn` gives the schema
   of an attribute."
  [field-schema-fn field-path]
  (and (root-field-path? field-path)
       (not (contains? excluded-data-types
                       (:field/data-type (field-schema-fn (first field-path)))))))

(defn card-config
  "The card config of the preview, for the entity type of the listing.

   `:kanban-card-config` is the card config of the Kanban for the entity type. Its first header
   field is the title of the preview. Its body and footer fields are the first rows, so the
   fields that the card shows are on top. `:accessible-field-paths` are all the fields that the
   user can read. Only these fields are asked for, because the search throws for a field that the
   user cannot read, also when the Kanban config names it. The rows are those of the fields that are
   `previewable-field-path?`, without a duplicate and without the title. `:pinned-fields` are the
   rows that the Kanban card shows. The preview shows them also when the record has no value,
   and the other rows only when it has one. `:currency-field-paths` are the fields that hold the
   currency of the values of a currency field, so that the preview shows the currency of the
   record. `:field-schema-fn` gives the schema of an attribute."
  [{:keys [kanban-card-config accessible-field-paths currency-field-paths field-schema-fn]}]
  (let [accessible (into #{} (keep normalize-field-path) accessible-field-paths)
        title-field (some-> (first (:header-fields kanban-card-config))
                            normalize-field-path)
        title-field (when (contains? accessible title-field)
                      title-field)
        row-fields (fn [field-paths]
                     (->> field-paths
                          (keep normalize-field-path)
                          (filter accessible)
                          (filter (partial previewable-field-path? field-schema-fn))
                          (remove #{title-field})
                          distinct
                          vec))
        kanban-fields (concat (:body-fields kanban-card-config)
                              (:footer-fields kanban-card-config))]
    {:header-fields   (if title-field
                        [title-field]
                        [])
     :body-fields     (row-fields (concat kanban-fields accessible-field-paths))
     :pinned-fields   (row-fields kanban-fields)
     :footer-fields   []
     :currency-fields (vec (keep normalize-field-path currency-field-paths))
     :show-labels?    true}))

(defn request-body
  "The body of the search request that loads one record. The query is the id of the entity in
   `system.db-id`, a field that every user may query. `card-config` is the card config of
   `card-config`. The request asks for the fields of the card config and for one hit."
  [{:keys [entity-type entity-id pipeline-id card-config]}]
  (cond-> {:entity-type        entity-type
           :opensearch-queries [{:term {"system.db-id" entity-id}}]
           :fields             (list-event/card-config->field-paths card-config)
           :from               0
           :size               1}
    (some? pipeline-id) (assoc :pipeline-id pipeline-id)))

(defn response->card
  "The card of the record from the response of the search, or nil when the response has no hit.
   `entity-id` is the id of the record that the preview waits for. A hit of another record is
   not the card."
  [{:keys [entity-id resp]}]
  (let [card (first (list-event/hits->items (get-in resp [:hits :hits])))]
    (when (and card
               (= entity-id (get card "original.db/id")))
      card)))

(defn pipeline-entity-type?
  "True when the entity type is a pipeline entity type: a deal, a fundraising
   opportunity, a sales opportunity or an exit opportunity. Only these types have
   a pipeline stage field configuration, so only these types get the editable
   preview."
  [entity-type]
  (contains? pipeline/entity-type-mappings entity-type))

(defn ref-id
  "The id of a reference value. The value is a map with `:db/id`, or the id
   itself. Nil stays nil."
  [value]
  (when (some? value)
    (or (:db/id value) value)))

(defn entity-pipeline-id
  "The id of the pipeline that the entity is in, from the pipeline attribute of
   its type, or nil."
  [entity]
  (some-> (get entity (pipeline/pipeline-key (:entity/type entity)))
          ref-id))

(defn entity-stage-id
  "The id of the stage that the entity is in, from the stage attribute of its
   type, or nil."
  [entity]
  (some-> (get entity (pipeline/stage-key (:entity/type entity)))
          ref-id))

(defn editor-ident
  "A stable ident for the creator-editor state of the editable preview of one
   record in one listing. The listing-id and the entity-id keep the state of two
   previews apart."
  [{:keys [listing-id entity-id]}]
  [::editor listing-id entity-id])

(defn editor-config-path
  "The path of the opportunity config inside the pipeline creator-config, for the
   editable preview of `entity-type` in `pipeline`."
  [{:keys [entity-type pipeline]}]
  (:main-config-path (ecu/resolve-config-paths {:entity-type entity-type
                                                :pipeline    pipeline})))

(defn invalid-edits?
  "True when an edited attribute has a validation error. `edits` is the creator
   entity-edits map (attribute to value). `validations` is the entity-validations
   map (attribute to errors). A validation error of a field that the user did not
   edit does not block the save of the fields that the user did edit."
  [{:keys [edits validations]}]
  (boolean (some (fn [attribute]
                   (contains? validations attribute))
                 (keys edits))))
